import type React from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

import { TeacherModals } from "constants/modals-constants";
import { TEACHER_HOME_PAGE_ROUTES } from "constants/routes";

import { useDomainHandler } from "hooks/useDomainHandler";
import { useModal } from "hooks/useModal";

import { Heading1 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { CssButton, CssFlex, CssIcon } from "styles/helpers/layout";

import { NavigationTab, NavigationTabContainer } from "components/NavigationTabs";
import { CreateEmailInviteModal } from "./components/CreateEmailInviteModal/CreateEmailInviteModal";

export const InviteManagementPage: React.FC = () => {
  const { t } = useTranslation("teacher-dashboard");
  const { getStudentTermByDomain } = useDomainHandler();

  const { openModal } = useModal();

  return (
    <CssFlex flexDirection="column" gap={1}>
      <Heading1>{t("invitationManagement.title")}</Heading1>
      <ParagraphLarge>
        {t("invitationManagement.description", {
          studentTerm: getStudentTermByDomain({ usePlural: true }),
        })}
      </ParagraphLarge>
      <CssFlex justifyContent="flex-end" gap={1} marginTop={2}>
        <CssButton
          dataCy="create-invite-bundle-button"
          variant="primary"
          onClick={() => openModal(TeacherModals.CreateEmailInviteBundle)}
        >
          <CssFlex alignItems="center" gap={0.5}>
            <CssIcon iconName="far fa-plus" />
            {t("invitationManagement.createInviteButton", {
              studentTerm: getStudentTermByDomain({ usePlural: true }),
            })}
          </CssFlex>
        </CssButton>
      </CssFlex>
      {/* -- Navigation tabs -- */}
      <NavigationTabContainer>
        <NavigationTab
          dataCy="invite-bundle-list-tab"
          icon="fal fa-paper-plane"
          activeIcon="fas fa-paper-plane"
          destinationUrl={TEACHER_HOME_PAGE_ROUTES.SENT_INVITE_BUNDLE_LIST}
        >
          {t("invitationManagement.tabs.bulkInvites")}
        </NavigationTab>
        <NavigationTab
          dataCy="invited-student-list-tab"
          icon="fal fa-users"
          activeIcon="fas fa-users"
          destinationUrl={TEACHER_HOME_PAGE_ROUTES.SENT_INVITE_LIST}
        >
          {t("invitationManagement.tabs.allInvitees", {
            studentTerm: getStudentTermByDomain({ usePlural: true }),
          })}
        </NavigationTab>
      </NavigationTabContainer>
      {/* -- Tab content will be rendered here -- */}
      <Outlet />
      {/* -- Invite creation modal -- */}
      <CreateEmailInviteModal />
    </CssFlex>
  );
};
